import type { Map, TileLayer } from 'leaflet';
import { tileLayer } from 'leaflet';
import { useEffect } from 'react';

import type {
  BackgroundLayerConfig,
  MapConfiguration
} from '@/common/overmind/namespaces/arcgis/config/config.types';
import { useActions, useAppState } from '@/overmind';

type BaseLayers = Record<BackgroundLayerConfig['id'], TileLayer>;

function getBaseLayers(layerConfigs: BackgroundLayerConfig[]) {
  const backgroundLayers: BaseLayers = {};

  layerConfigs.forEach(layer => {
    if (layer.type === 'tile') {
      backgroundLayers[layer.id] = tileLayer(layer.url, {
        attribution: layer.attribution,
        maxNativeZoom: layer.maxNativeZoom ?? 18,
        maxZoom: 25,
        className: layer.monochrome ? 'monochrome-map' : undefined
      });
    } else if (layer.type === 'wms') {
      backgroundLayers[layer.id] = tileLayer.wms(layer.url, {
        attribution: layer.attribution,
        maxNativeZoom: layer.maxNativeZoom ?? 18,
        layers: layer.layers,
        maxZoom: 25,
        className: layer.monochrome ? 'monochrome-map' : undefined
      });
    }
  });
  return backgroundLayers;
}

function getBaseLayer({
  backgroundLayers,
  defaultBackgroundLayerId
}: MapConfiguration['map']): TileLayer | undefined {
  const baseLayers = getBaseLayers(backgroundLayers);
  if (defaultBackgroundLayerId && baseLayers[defaultBackgroundLayerId]) {
    return baseLayers[defaultBackgroundLayerId];
  } else {
    return Object.values(baseLayers)[0];
  }
}

export function useBaseMapLayer(map: Map | null) {
  const { backgroundMapReady, backgroundMapNotReady } = useActions().map;
  const config = useAppState().arcgis.config.value;

  useEffect(() => {
    let baseLayer: TileLayer | undefined;
    if (config && map) {
      baseLayer = getBaseLayer(config.map);
      if (baseLayer) {
        baseLayer.addTo(map);
        backgroundMapReady();
      }
    }

    return () => {
      if (baseLayer && map && map.hasLayer(baseLayer)) {
        backgroundMapNotReady();
        map.removeLayer(baseLayer);
      }
    };
  }, [config, map]);
}
